import { forwardRef } from 'react';
import { SilasSeal } from './SilasSeal';

/**
 * SilasButton - Outlined by default, tint on hover, fill on press
 *
 * DOCTRINE:
 * - Hover is NOT commitment. Hover gets a tint, not a fill.
 * - Filled means committed - fill on press/active only
 * - With-seal: standard button + 12px seal badge inline (NOT oxblood fill)
 * - NEVER use bg-[--authority] or oxblood on buttons. Oxblood is for seals only.
 */

export type SilasButtonVariant = 'primary' | 'secondary' | 'with-seal';
export type SilasButtonSize = 'sm' | 'md' | 'lg';

export interface SilasButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: SilasButtonVariant;
  /** Button size */
  size?: SilasButtonSize;
  /** Loading state - subtle pulsing opacity, disables pointer events */
  loading?: boolean;
  /** Glow highlight for attention */
  glowing?: boolean;
  /** Seal tooltip (required when variant="with-seal") */
  sealTooltip?: string;
  /** Override the end icon (used for with-seal variant) */
  endIcon?: React.ReactNode;
  /** Children */
  children: React.ReactNode;
}

export const SilasButton = forwardRef<HTMLButtonElement, SilasButtonProps>(function SilasButton(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    glowing = false,
    sealTooltip,
    endIcon,
    children,
    className = '',
    disabled,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading;

  // Size-specific styles (floors raised from 9/10/11px — uppercase micro-type
  // below ~11px failed legibility in live review). min-h keeps every size at
  // a ≥36px hit target even where the type stays small.
  const sizeStyles: Record<SilasButtonSize, string> = {
    sm: 'min-h-9 px-2.5 py-1.5 text-[11px]',
    md: 'min-h-9 px-3.5 py-2 text-[12px]',
    lg: 'min-h-10 px-5 py-2.5 text-[13px]',
  };

  // Base styles shared by all variants
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    uppercase tracking-[0.15em] font-medium rounded-sm
    transition-all duration-150 ease-out
    focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-1 focus:ring-offset-[var(--bg)]
    disabled:opacity-40 disabled:cursor-not-allowed
  `.trim();

  // Variant-specific styles
  const variantStyles: Record<SilasButtonVariant, string> = {
    primary: `
      border border-[var(--accent)]
      text-[var(--accent)]
      bg-transparent
      hover:bg-[var(--accent-dim)]
      active:bg-[var(--accent)] active:text-[var(--bg)]
    `.trim(),
    secondary: `
      border border-[var(--text-muted)]
      text-[var(--text-secondary)]
      bg-transparent
      hover:border-[var(--text-secondary)] hover:text-[var(--text-primary)]
      active:bg-[var(--surface-hover)]
    `.trim(),
    'with-seal': `
      border border-[var(--accent)]
      text-[var(--accent)]
      bg-transparent
      hover:bg-[var(--accent-dim)]
      active:bg-[var(--accent)] active:text-[var(--bg)]
    `.trim(),
  };

  // Loading animation style
  const loadingStyles = loading ? 'animate-pulse pointer-events-none' : '';

  const glowStyles = glowing ? 'shadow-[0_0_20px_rgba(212,165,116,0.3)] border-[#D4A574]' : '';

  return (
    <button
      ref={ref}
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${loadingStyles} ${glowStyles} ${className}`}
      disabled={isDisabled}
      {...props}
    >
      {children}
      {variant === 'with-seal' &&
        !loading &&
        (endIcon || (
          <SilasSeal size={12} state="full" tooltip={sealTooltip || 'Silas recommendation'} />
        ))}
    </button>
  );
});

SilasButton.displayName = 'SilasButton';

export default SilasButton;
